/**
 * AbandonedOrderListPage.js - 弃单管理页
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Popconfirm,
  message,
  Tooltip,
  Input,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminAbandonedOrderApi } from '../../api/index';

const { Title } = Typography;
const { Search } = Input;

export default function AbandonedOrderListPage() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [keyword, setKeyword] = useState('');

  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAbandonedOrderApi.list({ page, pageSize, keyword });
      if (Array.isArray(data)) {
        setOrders(data);
        setTotal(data.length);
      } else {
        setOrders(data?.list || data?.items || []);
        setTotal(data?.total || 0);
      }
    } catch (err) {
      message.error('加载弃单列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // 恢复弃单
  const handleRecover = async (id) => {
    try {
      await adminAbandonedOrderApi.recover(id);
      message.success('弃单已恢复');
      fetchOrders();
    } catch (err) {
      message.error('恢复失败：' + (err.message || '未知错误'));
    }
  };

  // 删除弃单
  const handleDelete = async (id) => {
    try {
      await adminAbandonedOrderApi.delete(id);
      message.success('弃单已删除');
      fetchOrders();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 查看详情
  const handleViewDetail = (id) => {
    navigate(`/admin/abandoned-orders/${id}`);
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (no) => <span style={{ fontFamily: 'monospace' }}>{no}</span>,
    },
    {
      title: '用户邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          abandoned: 'orange',
          recovered: 'green',
        };
        return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: '总金额',
      key: 'total',
      render: (_, record) => `$${(record.total || 0).toFixed(2)}`,
    },
    {
      title: '弃单时间',
      dataIndex: 'abandonedAt',
      key: 'abandonedAt',
      render: (t) => t ? new Date(t).toLocaleString() : '-',
    },
    {
      title: '恢复时间',
      dataIndex: 'recoveredAt',
      key: 'recoveredAt',
      render: (t) => t ? new Date(t).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetail(record.id);
              }}
            />
          </Tooltip>
          {record.status !== 'recovered' && (
            <Tooltip title="恢复">
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecover(record.id);
                }}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="确认删除该弃单？"
            onConfirm={() => handleDelete(record.id)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          弃单管理
        </Title>
        <Search
          placeholder="搜索订单号或邮箱"
          style={{ width: 300 }}
          onSearch={setKeyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
        />
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: '暂无弃单' }}
          onRow={(record) => ({
            onClick: () => handleViewDetail(record.id),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
}
